import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { formatISTDateTime } from "@/lib/time";

// Detect contact's source channel for badging + send routing.
function channelMeta(contact) {
  const ch = contact?.channel;
  const phone = contact?.phone || "";
  if (ch === "messenger" || phone.startsWith("fb:")) {
    return { key: "messenger", label: "Messenger", iconText: "M",
             badgeBg: "bg-[#0084FF]", addressLabel: "via Messenger" };
  }
  if (ch === "instagram" || phone.startsWith("ig:")) {
    return { key: "instagram", label: "Instagram", iconText: "IG",
             badgeBg: "bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF]",
             addressLabel: "via Instagram" };
  }
  return { key: "whatsapp", label: "WhatsApp", iconText: "W",
           badgeBg: "bg-[#25D366]", addressLabel: null };
}
import EmptyHero from "@/components/hub/EmptyHero";
import MediaBubble from "@/components/hub/MediaBubble";
import ChatActivityDialog from "@/components/hub/ChatActivityDialog";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import {
  ChatsTeardrop,
  Phone,
  Eye,
  Lock,
  PaperPlaneTilt,
  Check,
  Checks,
  X,
  BookmarkSimple,
  Plus,
  Trash,
  MagnifyingGlass,
  DownloadSimple,
  Tag,
  UserCircle,
  CircleNotch,
  Paperclip,
  Smiley,
  DotsThree,
  CaretLeft,
  ClipboardText,
  Robot,
  CheckCircle,
  ArrowCounterClockwise,
  Hash,
  Siren,
  Info,
  Heart,
  Clock,
  PencilSimple,
  Warning,
  Megaphone,
  ArrowBendUpLeft,
} from "@phosphor-icons/react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

function initials(name, phone) {
  const src = (name || phone || "?").trim();
  return src.split(/\s+/).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
}

function EmptyState() {
  return <EmptyHero />;
}

function Tick({ status, failure }) {
  // outgoing status: sending, sent, delivered, read, failed
  if (status === "sending")
    return (
      <Clock
        size={14}
        weight="bold"
        className="text-white/50 animate-pulse"
        data-testid="tick-sending"
      />
    );
  if (status === "read") return <Checks size={14} weight="bold" className="text-[#60A5FA]" />;
  if (status === "delivered") return <Checks size={14} weight="bold" className="text-white/60" />;
  if (status === "failed") {
    // Per user rule 2026-02-14 — surface Meta's failure reason (error
    // code + title) as a tooltip on the red ✕ so the agent knows WHY
    // delivery failed. Common reasons: customer not on WhatsApp,
    // user blocked the business, 24h re-engagement rule, etc.
    const reason = failure?.code
      ? `Delivery failed · ${failure.code}: ${failure.title || failure.message || "Meta rejected the message"}`
      : "Delivery failed";
    return (
      <X
        size={14}
        weight="bold"
        className="text-[#FCA5A5] cursor-help"
        data-testid="tick-failed"
        title={reason}
      />
    );
  }
  return <Check size={14} weight="bold" className="text-white/60" />;
}

export default function ChatWindow({ contact, messages, onBack, mobileVisible = false, viewers = [], claim = null, othersTyping = null, onCreateBooking, onLoadOlder, addOptimistic, replaceOptimistic, removeOptimistic }) {
  const { user: me } = useAuth();
  const endRef = useRef(null);
  const scrollRef = useRef(null);
  const typingFiredRef = useRef({}); // message_id -> last-fired timestamp for throttling
  const inputRef = useRef(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [triggeringBot, setTriggeringBot] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [users, setUsers] = useState([]);
  const [inChatQuery, setInChatQuery] = useState("");
  const [botState, setBotState] = useState(null);
  // Per user rule 2026-02-14 — the "Bot is handling this chat / Takeover
  // from bot" banner must:
  //   • Disappear when the ticket is resolved (bot finished its flow,
  //     chat is silently closed — no action needed from agent).
  //   • Auto-hide after 5 minutes of inactivity since the LAST bot reply
  //     OR last customer message. Earlier the banner stayed up for 24h
  //     after the bot had clearly finished, creating phantom work for
  //     agents who had to "Take over" + "Close" chats that were already
  //     dead.
  //   • Re-appear automatically when the customer interacts with the bot
  //     again (last_active gets refreshed on the next inbound) — handled
  //     organically by loadBotState + the SSE-driven re-fetch.
  // The `tick` state forces a re-render every 30s so the 5-min cut-off
  // is evaluated live without needing a fresh server call.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30 * 1000);
    return () => clearInterval(id);
  }, []);
  const [activityOpen, setActivityOpen] = useState(false);
  const [olderLoading, setOlderLoading] = useState(false);
  const [olderHasMore, setOlderHasMore] = useState(true);
  // Admin-only `managment_feedback` quick-send popup state
  // (per user rule 2026-02-13). Opens when the megaphone icon next to
  // the call button is clicked. Fetches `default_body_params` from the
  // backend on open so {{1}} is pre-filled with the admin's first name
  // and {{2}} with their designation (per real template body —
  // sender introduces themselves to the customer).
  const [mfOpen, setMfOpen] = useState(false);
  const [mfParams, setMfParams] = useState(["", ""]);
  const [mfLabels, setMfLabels] = useState(["Your name", "Your designation"]);
  const [mfPreview, setMfPreview] = useState("");
  const [mfSending, setMfSending] = useState(false);
  const [mfLoadingPreview, setMfLoadingPreview] = useState(false);
  // Per user rule 2026-02-13 — Reply-to-message (quote reply).
  // When agent clicks the curved-arrow icon on any message bubble, that
  // message becomes `replyingTo` and a quoted-preview banner appears
  // above the composer. On send, the wamid is passed as
  // `reply_to_message_id` so Meta renders a quoted bubble on the
  // customer's side and the chat UI on our side shows the same quoted
  // preview above the outgoing message.
  const [replyingTo, setReplyingTo] = useState(null);
  // Initial-history loading flag — flips ON when a chat is selected with no
  // messages yet, OFF as soon as the first batch arrives or after a 4s max
  // wait so a stuck state never deadlocks the UI. Drives the unique
  // RodBez-themed full-canvas loader below.
  const [initialLoading, setInitialLoading] = useState(false);
  // Shortcut autocomplete state
  const [shortcuts, setShortcuts] = useState([]);
  const [ac, setAc] = useState({ open: false, query: "", start: -1, index: 0 });

  // Translation: per-message translated text cache (key: message id)
  const [translations, setTranslations] = useState({}); // { [msgId]: {loading, translated, error} }
  // Composer auto-translate (Hindi/Hinglish typed → English sent)
  const [composerTranslate, setComposerTranslate] = useState(false);
  const [composerPreview, setComposerPreview] = useState({
    loading: false,
    translated: "",
    sourceText: "",
  });
  const composerTranslateTimerRef = useRef(null);
  const translateMessage = async (msgId, srcText) => {
    if (!msgId || !srcText) return;
    if (translations[msgId]?.translated) return;
    setTranslations((prev) => ({ ...prev, [msgId]: { loading: true } }));
    try {
      // Inbound message → Hindi (Devanagari) for the agent to read in their
      // preferred language. (English-language inbound messages will be
      // translated to Hindi too — agent preference.)
      const r = await api.post("/translate", { text: srcText, target_lang: "hi" });
      setTranslations((prev) => ({
        ...prev,
        [msgId]: { loading: false, translated: r.data.translated },
      }));
    } catch (e) {
      setTranslations((prev) => ({
        ...prev,
        [msgId]: { loading: false, error: e?.response?.data?.detail || "Translation failed" },
      }));
    }
  };

  // --- Typing indicator: throttle typing=true to ~once per 2s, and send
  // typing=false on blur / send / switch / 3s idle. Stateless on the server.
  const typingStateRef = useRef({ active: false, lastFired: 0, idleTimer: null, phone: null });
  const sendTyping = (phone, flag) => {
    if (!phone) return;
    api.post(`/conversations/${phone}/typing`, { phone, typing: !!flag }).catch(() => {});
  };
  const bumpTyping = () => {
    const phone = contact?.phone;
    if (!phone) return;
    const state = typingStateRef.current;
    const now = Date.now();
    if (state.phone && state.phone !== phone && state.active) {
      // Chat switched while still "typing" on the previous one — clear that.
      sendTyping(state.phone, false);
      state.active = false;
    }
    state.phone = phone;
    if (!state.active || now - state.lastFired > 2000) {
      sendTyping(phone, true);
      state.active = true;
      state.lastFired = now;
    }
    if (state.idleTimer) clearTimeout(state.idleTimer);
    state.idleTimer = setTimeout(() => {
      if (state.phone) sendTyping(state.phone, false);
      state.active = false;
    }, 3000);
  };
  const stopTyping = () => {
    const state = typingStateRef.current;
    if (state.idleTimer) clearTimeout(state.idleTimer);
    if (state.active && state.phone) sendTyping(state.phone, false);
    state.active = false;
  };
  // On chat change or unmount: fire typing=false for the previous chat
  useEffect(() => {
    return () => stopTyping();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact?.phone]);

  // --- Read receipts: signal that this agent is actively viewing `phone`.
  // Fires viewing=true on mount/switch, viewing=false on tab-hide, tab-close,
  // switch to another chat, or unmount. Lightweight — backend tracks viewers
  // in-memory and broadcasts a `viewing` SSE event on every change.
  useEffect(() => {
    const phone = contact?.phone;
    if (!phone) return;
    const send = (flag) => {
      api.post(`/conversations/${phone}/viewing`, { phone, viewing: flag }).catch(() => {});
    };
    send(true);
    const onVis = () => send(!document.hidden);
    document.addEventListener("visibilitychange", onVis);
    const onHide = () => {
      try {
        const url = `${API_BASE}/conversations/${phone}/viewing`;
        const body = JSON.stringify({ phone, viewing: false });
        // Best-effort on tab close (sendBeacon can't set auth headers so we
        // also fire a regular request which usually completes before unload).
        navigator.sendBeacon?.(url, new Blob([body], { type: "application/json" }));
      } catch (_) {}
      send(false);
    };
    window.addEventListener("pagehide", onHide);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", onHide);
      send(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact?.phone]);

  const loadBotState = async () => {
    if (!contact?.phone) return setBotState(null);
    try {
      const r = await api.get(`/bot/state/${contact.phone}`);
      setBotState(r.data);
    } catch (_) {
      setBotState(null);
    }
  };

  useEffect(() => {
    loadBotState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact?.phone]);

  // Auto-fetch RodBez customer identity ONCE per contact when the chat is
  // opened. The backend caches the result on the contact row
  // (`rodbez_name_verified=true`) so subsequent opens skip the upstream
  // API call entirely. Only fires for contacts that haven't been verified
  // yet AND haven't been attempted-and-failed in this browser session.
  const lookupAttemptedRef = useRef(new Set());
  useEffect(() => {
    if (!contact?.phone) return;
    if (contact?.rodbez_name_verified) return;
    if (lookupAttemptedRef.current.has(contact.phone)) return;
    lookupAttemptedRef.current.add(contact.phone);
    api.post(`/rodbez/customer/lookup/${contact.phone}`).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact?.phone, contact?.rodbez_name_verified]);

  // Refresh bot state whenever a new message arrives in this chat, and
  // every 10s as a safety net. This ensures the "Bot is handling" overlay
  // appears within seconds of a visual-bot session being created, without
  // requiring the agent to re-open the chat.
  useEffect(() => {
    if (!contact?.phone) return;
    loadBotState();
    const interval = setInterval(loadBotState, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact?.phone, messages.length]);

  // Composer live translation: as agent types Hindi/Hinglish, debounce 500ms
  // then call /api/translate to show the English version they're about to
  // send. Only fires when the toggle is ON.
  useEffect(() => {
    if (composerTranslateTimerRef.current) {
      clearTimeout(composerTranslateTimerRef.current);
    }
    if (!composerTranslate || !text.trim()) {
      setComposerPreview({ loading: false, translated: "", sourceText: "" });
      return;
    }
    setComposerPreview((p) => ({ ...p, loading: true }));
    composerTranslateTimerRef.current = setTimeout(async () => {
      try {
        const r = await api.post("/translate", { text: text.trim(), target_lang: "en" });
        setComposerPreview({
          loading: false,
          translated: r.data.translated,
          sourceText: text.trim(),
        });
      } catch (_) {
        setComposerPreview({ loading: false, translated: "", sourceText: "" });
      }
    }, 1000);  // 1s debounce — typing pause hone par hi API hit, cost optimized
    return () => {
      if (composerTranslateTimerRef.current) clearTimeout(composerTranslateTimerRef.current);
    };
  }, [text, composerTranslate]);

  // When agent toggles Auto-translate OFF, clear preview immediately.
  useEffect(() => {
    if (!composerTranslate) {
      setComposerPreview({ loading: false, translated: "", sourceText: "" });
    }
  }, [composerTranslate]);

  const botPauseResume = async (action) => {
    if (!contact?.phone) return;
    try {
      await api.post(`/bot/${action}/${contact.phone}`);
      toast.success(`Bot ${action}d`);
      loadBotState();
    } catch (_) {
      toast.error("Bot action failed");
    }
  };

  // Auto-scroll: jump to bottom on chat switch + when a NEW message arrives
  // at the bottom (newest_ts changes). When older messages are prepended at
  // the top via loadOlder, we restore scroll position there separately.
  const lastMsgTsRef = useRef(null);
  useEffect(() => {
    const last = messages[messages.length - 1];
    const lastTs = last?.timestamp || null;
    const phoneChanged = !lastMsgTsRef.current || lastMsgTsRef.current.phone !== contact?.phone;
    const newestChanged =
      !phoneChanged && lastMsgTsRef.current?.ts !== lastTs;
    if (phoneChanged || newestChanged) {
      endRef.current?.scrollIntoView({
        behavior: phoneChanged ? "auto" : "smooth",
        block: "end",
      });
    }
    lastMsgTsRef.current = { phone: contact?.phone, ts: lastTs };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, contact?.phone]);

  // Reset older-pagination flags whenever the chat changes
  useEffect(() => {
    setOlderHasMore(true);
    setOlderLoading(false);
  }, [contact?.phone]);

  // Initial-load loader lifecycle
  useEffect(() => {
    if (!contact?.phone) {
      setInitialLoading(false);
      return;
    }
    // Show the loader as soon as a chat is selected with an empty list.
    // It will be cleared by the next effect once messages arrive, or by a
    // 4-second safety timer so users never see a stuck spinner.
    if (messages.length === 0) {
      setInitialLoading(true);
      const t = setTimeout(() => setInitialLoading(false), 4000);
      return () => clearTimeout(t);
    } else {
      setInitialLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact?.phone]);

  // Clear the initial loader the moment messages arrive for the active chat.
  useEffect(() => {
    if (initialLoading && messages.length > 0) setInitialLoading(false);
  }, [messages.length, initialLoading]);

  // Scroll-to-load-older: when the user scrolls near the top of the
  // messages container, fetch the next older page via onLoadOlder. Preserve
  // visual scroll position so the user doesn't jump after older messages
  // get prepended.
  const handleScroll = async (e) => {
    if (!onLoadOlder) return;
    if (olderLoading || !olderHasMore) return;
    const el = e.currentTarget;
    if (el.scrollTop > 60) return; // not near top
    const prevScrollHeight = el.scrollHeight;
    const prevScrollTop = el.scrollTop;
    setOlderLoading(true);
    try {
      const res = await onLoadOlder();
      const hasMore = res?.hasMore ?? true;
      setOlderHasMore(hasMore);
      // Restore scroll: keep the same row visible
      requestAnimationFrame(() => {
        const newH = el.scrollHeight;
        el.scrollTop = newH - prevScrollHeight + prevScrollTop;
      });
    } finally {
      setOlderLoading(false);
    }
  };

  useEffect(() => {
    api.get("/templates").then((r) => setTemplates(r.data)).catch(() => {});
    // Use /auth/users (admin) when admin, else /auth/users/assignable (open to all)
    api
      .get("/auth/users")
      .then((r) => setUsers(r.data))
      .catch(() =>
        api
          .get("/auth/users/assignable")
          .then((r) => setUsers(r.data))
          .catch(() => setUsers([])),
      );
    api.get("/shortcodes").then((r) => setShortcuts(r.data || [])).catch(() => setShortcuts([]));
  }, []);

  // Detect a #shortcode token immediately before the cursor.
  // Returns { start, query } when a token is being typed, else null.
  const detectShortcodeToken = (value, cursorPos) => {
    if (!value || cursorPos == null) return null;
    const before = value.slice(0, cursorPos);
    // Match a # at word boundary, followed by word chars up to cursor (no space)
    const m = before.match(/(?:^|\s)(#[a-zA-Z0-9_]*)$/);
    if (!m) return null;
    const token = m[1];
    const start = before.length - token.length;
    return { start, query: token.slice(1).toLowerCase() };
  };

  const acMatches = useMemo(() => {
    if (!ac.open) return [];
    const q = ac.query;
    const list = shortcuts.filter((s) => s.code.slice(1).toLowerCase().startsWith(q));
    return list.slice(0, 8);
  }, [ac, shortcuts]);

  const updateText = (newValue, cursorPos) => {
    setText(newValue);
    // Emit typing signal while user is composing (throttled to ~1/2s)
    if (newValue && newValue.trim().length > 0) {
      bumpTyping();
    } else {
      stopTyping();
    }
    const tok = detectShortcodeToken(newValue, cursorPos);
    if (tok) {
      setAc({ open: true, query: tok.query, start: tok.start, index: 0 });
    } else if (ac.open) {
      setAc((a) => ({ ...a, open: false }));
    }
  };

  const insertShortcut = (sc) => {
    if (!sc) return;
    const cur = inputRef.current;
    const cursorPos = cur ? cur.selectionStart : text.length;
    const start = ac.start >= 0 ? ac.start : cursorPos;
    const before = text.slice(0, start);
    const after = text.slice(cursorPos);
    const inserted = sc.reply;
    const newText = before + inserted + after;
    setText(newText);
    setAc({ open: false, query: "", start: -1, index: 0 });
    // restore focus + cursor at end of inserted text
    requestAnimationFrame(() => {
      const el = inputRef.current;
      if (el) {
        const newCursor = before.length + inserted.length;
        el.focus();
        try {
          el.setSelectionRange(newCursor, newCursor);
        } catch (_) {}
      }
    });
  };

  const handleInputKeyDown = (e) => {
    if (ac.open && acMatches.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setAc((a) => ({ ...a, index: (a.index + 1) % acMatches.length }));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setAc((a) => ({ ...a, index: (a.index - 1 + acMatches.length) % acMatches.length }));
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        insertShortcut(acMatches[ac.index]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setAc((a) => ({ ...a, open: false }));
        return;
      }
    }
    // Send on Enter (without Shift); Shift+Enter inserts a newline
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const filteredMessages = useMemo(() => {
    const q = inChatQuery.trim().toLowerCase();
    if (!q) return messages;
    return messages.filter((m) => (m.text || "").toLowerCase().includes(q));
  }, [messages, inChatQuery]);

  // Gate: agent MUST send a greeting (containing "my name is") as their first
  // outgoing message in this chat. Until then, all sends/uploads are blocked.
  // Releases automatically once this agent has sent at least one outgoing msg.
  const mustGreetFirst = useMemo(() => {
    if (!contact || !me) return false;
    // Per user rule 2026-02 — agent ka FIRST reply hamesha greeting
    // include karega (chahe bot mid-flow ho ya already greet kar chuka
    // ho). Bot ka greet alag entity hai, agent ka apna introduction
    // alag.
    const myId = me?.id;
    const iHaveReplied = (messages || []).some(
      (m) => m?.direction === "outgoing" && m?.sent_by?.id === myId,
    );
    return !iHaveReplied;
  }, [contact, me, messages]);

  // Soft Lock — per user rule 2026-02-11. When ANOTHER agent replied
  // to this same chat within the last 5 minutes, show a yellow banner
  // and require a confirm-takeover before send. The actual takeover
  // happens server-side via the reply-takeover assignment logic; this
  // is a politeness gate so two agents don't accidentally talk over
  // each other on busy floors.
  const SOFT_LOCK_WINDOW_MS = 5 * 60 * 1000;
  const softLockInfo = useMemo(() => {
    if (!me || !messages?.length) return null;
    const myId = me.id;
    // Walk newest → oldest, find the most recent OUTGOING message
    // (skip bot/system messages without sent_by).
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m?.direction !== "outgoing") continue;
      const sb = m?.sent_by;
      if (!sb?.id) continue; // bot/system — no agent owner
      if (sb.id === myId) return null; // my own most-recent reply → no lock
      const ts = m?.timestamp ? new Date(m.timestamp).getTime() : 0;
      const ago = Date.now() - ts;
      if (ago > 0 && ago < SOFT_LOCK_WINDOW_MS) {
        return {
          agentName: sb.full_name || sb.email || "another agent",
          agentId: sb.id,
          minutesAgo: Math.max(1, Math.round(ago / 60000)),
        };
      }
      return null; // most recent agent reply is stale → no lock
    }
    return null;
  }, [me, messages]);
  const [takeoverConfirmed, setTakeoverConfirmed] = useState(false);
  // Reset takeover confirmation when the chat changes OR when the soft
  // lock disappears on its own (other agent's reply ages past 5 min).
  useEffect(() => {
    setTakeoverConfirmed(false);
  }, [contact?.phone, softLockInfo?.agentId]);

  const isGreetingMessage = (s) => /my name is/i.test(s || "");

  // ---- Book Now: one-click booking bot trigger ----------------------
  const triggerBookingBot = async () => {
    if (!contact?.phone || triggeringBot) return;
    const phone = contact.phone;
    setTriggeringBot(true);
    try {
      await api.post("/booking-bot/trigger", { to: phone });
      toast.success("Booking bot started — customer ko menu bheja gaya 🚖");
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = (typeof d === "string" ? d : d?.message) || e.message || "Failed to trigger";
      toast.error(msg);
    } finally {
      setTriggeringBot(false);
    }
  };

  const isFb = (contact?.phone || "").startsWith("fb:");
  const isIg = (contact?.phone || "").startsWith("ig:");

  const send = async () => {
    let body = text.trim();
    if (!body || !contact || sending) return;
    // Greeting gate — block any outgoing reply until the agent introduces
    // themselves with a "My name is …" line. This ensures every customer
    // gets a proper greeting from the assigned agent before any other reply.
    if (mustGreetFirst && !isGreetingMessage(body)) {
      toast.error("Pehle customer ko greet karein", {
        description: "Greeting card se 'Send' click karein, ya apna message me 'My name is …' include karein.",
      });
      return;
    }
    // Soft Lock — confirm takeover when another agent replied recently.
    if (softLockInfo && !takeoverConfirmed) {
      const ok = window.confirm(
        `⚠️ ${softLockInfo.agentName} replied ${softLockInfo.minutesAgo} min ago.\n\n` +
        `Are you sure you want to take over this chat?`
      );
      if (!ok) return;
      setTakeoverConfirmed(true);
    }
    // If composer auto-translate is ON and we have a fresh translation that
    // matches the current text, send the TRANSLATED text instead.
    if (
      composerTranslate &&
      composerPreview.translated &&
      composerPreview.sourceText === body
    ) {
      body = composerPreview.translated;
    } else if (composerTranslate && body) {
      // No fresh translation yet — translate now synchronously before send.
      try {
        const r = await api.post("/translate", { text: body, target_lang: "en" });
        if (r.data?.translated) body = r.data.translated;
      } catch (_) {
        // fall through and send original
      }
    }
    setSending(true);
    const phone = contact.phone || "";
    // ---- Optimistic insert (instant UI) -------------------------------
    // Bubble appears immediately with status="sending" (clock icon). On
    // success → swap temp id for real wamid + status="sent". On failure
    // → mark status="failed" with error tooltip.
    const tempId = `optim_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    // Snapshot the reply target BEFORE we clear it (so the optimistic
    // bubble + API call both use it consistently).
    const replyToWamid = replyingTo?.id || null;
    const optimMsg = {
      id: tempId,
      tempId,
      from: phone,
      name: null,
      text: body,
      type: "text",
      timestamp: new Date().toISOString(),
      direction: "outgoing",
      status: "sending",
      ...(replyToWamid ? { replied_to: replyToWamid } : {}),
      sent_by: me ? {
        id: me.id,
        email: me.email,
        full_name: me.full_name,
      } : null,
    };
    addOptimistic && addOptimistic(optimMsg);
    // Clear the composer right away — the user already sees their text
    // in the bubble, no point keeping it in the input.
    setText("");
    setComposerPreview({ loading: false, translated: "", sourceText: "" });
    setReplyingTo(null);
    stopTyping();
    try {
      let resp;
      if (phone.startsWith("fb:")) {
        resp = await api.post("/messenger/send", { phone, text: body });
      } else if (phone.startsWith("ig:")) {
        resp = await api.post("/instagram/send", { phone, text: body });
      } else {
        resp = await api.post("/reply", {
          to: phone,
          text: body,
          ...(replyToWamid ? { reply_to_message_id: replyToWamid } : {}),
        });
      }
      const realId = resp?.data?.message_id || resp?.data?.id;
      replaceOptimistic && replaceOptimistic(phone, tempId, {
        id: realId || tempId,
        status: "sent",
      });
      toast.success("Reply sent");
    } catch (e) {
      const d = e?.response?.data?.detail;
      const errMsg = (d && (d.hint || d.message)) || (typeof d === "string" ? d : null) || e.message || "Failed to send";
      replaceOptimistic && replaceOptimistic(phone, tempId, {
        status: "failed",
        error: typeof errMsg === "string" ? errMsg : JSON.stringify(errMsg),
      });
      toast.error(typeof errMsg === "string" ? errMsg : JSON.stringify(errMsg));
    } finally {
      setSending(false);
    }
  };

  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const uploadAndSend = async (file) => {
    if (!file || !contact) return;
    // Greeting gate — block any media upload before the agent has greeted.
    if (mustGreetFirst) {
      toast.error("Pehle customer ko greet karein", {
        description: "File bhejne se pehle greeting send karna zaroori hai.",
      });
      return;
    }
    setUploading(true);
    const phone = contact.phone || "";
    const kind = file.type.startsWith("image/") ? "image"
      : file.type.startsWith("video/") ? "video"
      : file.type.startsWith("audio/") ? "audio"
      : "document";
    // ---- Optimistic placeholder (instant UI) -------------------------
    // Show a "Sending <kind>…" bubble right away so the agent gets
    // visual feedback during upload+send (which can take 5-15s for
    // larger files).
    const tempId = `optim_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const captionNow = text.trim();
    const placeholderLabel = kind === "image" ? "📷 Sending image…"
      : kind === "video" ? "🎬 Sending video…"
      : kind === "audio" ? "🎵 Sending audio…"
      : `📎 Sending ${file.name || "document"}…`;
    addOptimistic && addOptimistic({
      id: tempId,
      tempId,
      from: phone,
      name: null,
      text: captionNow ? `${placeholderLabel}\n${captionNow}` : placeholderLabel,
      type: "text",
      timestamp: new Date().toISOString(),
      direction: "outgoing",
      status: "sending",
      sent_by: me ? { id: me.id, email: me.email, full_name: me.full_name } : null,
    });
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("wh_token");
      const up = await fetch(`${API_BASE}/media/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!up.ok) throw new Error("Upload failed");
      const upData = await up.json();
      const { media_id, public_url } = upData;
      let resp;
      if (phone.startsWith("fb:") || phone.startsWith("ig:")) {
        // Messenger/Instagram require a publicly-fetchable URL, not media_id.
        if (!public_url) {
          throw new Error("Media upload returned no public URL — required for Messenger/IG");
        }
        const endpoint = phone.startsWith("fb:") ? "/messenger/send" : "/instagram/send";
        resp = await api.post(endpoint, {
          phone,
          attachment_url: public_url,
          attachment_type: kind === "document" ? "file" : kind,
        });
      } else {
        resp = await api.post("/reply/media", {
          to: phone,
          media_id,
          kind,
          caption: captionNow || undefined,
          filename: kind === "document" ? file.name : undefined,
        });
      }
      // The real media bubble (with proxy URL) will arrive via SSE.
      // Drop the placeholder now so we don't show two bubbles.
      const realId = resp?.data?.message_id || resp?.data?.id;
      if (realId) {
        // Best-effort: leave placeholder marked sent; SSE will merge on
        // realId arrival and add the rich media bubble alongside.
        replaceOptimistic && replaceOptimistic(phone, tempId, {
          status: "sent",
        });
        // Then remove the text placeholder — the media bubble arriving
        // via SSE renders the full preview.
        setTimeout(() => {
          removeOptimistic && removeOptimistic(phone, tempId);
        }, 800);
      } else {
        removeOptimistic && removeOptimistic(phone, tempId);
      }
      setText("");
      stopTyping();
      toast.success(`${kind} sent`);
    } catch (e) {
      const errMsg = e?.response?.data?.detail?.message || e.message || "Send failed";
      replaceOptimistic && replaceOptimistic(phone, tempId, {
        status: "failed",
        error: typeof errMsg === "string" ? errMsg : JSON.stringify(errMsg),
      });
      toast.error(errMsg);
    } finally {
      setUploading(false);
    }
  };

  const react = async (message_id, emoji) => {
    try {
      await api.post("/reactions", { to: contact.phone, message_id, emoji });
    } catch (_) {
      toast.error("Reaction failed");
    }
  };

  // Typing indicator — fire ONCE per inbound message_id when agent starts typing.
  // Meta accepts only one "mark-read + typing" call per message, so we MUST throttle.
  // The typing indicator stays visible on customer's WhatsApp for ~25 seconds
  // (or until the agent sends the reply — whichever comes first).
  useEffect(() => {
    if (!contact || !text) return;
    const lastIn = [...messages].reverse().find((m) => m.direction === "inbound");
    if (!lastIn || !lastIn.id || !lastIn.id.startsWith("wamid")) return;
    // Only fire once per message_id, and re-arm 25s later (Meta auto-dismisses after ~25s)
    const firedKey = `typed:${lastIn.id}`;
    const lastFired = typingFiredRef.current[firedKey] || 0;
    if (Date.now() - lastFired < 25_000) return;
    const t = setTimeout(() => {
      typingFiredRef.current[firedKey] = Date.now();
      api.post("/typing", { message_id: lastIn.id })
        .then((r) => {
          if (!r.data?.ok && r.data?.reason) {
            console.debug("[typing] skipped:", r.data.reason);
          } else if (!r.data?.ok && r.data?.error) {
            console.debug("[typing] meta error:", r.data.error);
          }
        })
        .catch((e) => console.debug("[typing] request failed:", e?.message));
    }, 800);
    return () => clearTimeout(t);
  }, [text, contact?.phone]);

  const updateStatus = async (status, { silent = false } = {}) => {
    try {
      const { data } = await api.patch(
        `/conversations/${contact.phone}/status`,
        { status, silent },
      );
      if (status === "resolved") {
        const greeting = data?.auto_greeting;
        if (silent) {
          toast.success("Marked as Resolved (silent — no message sent)");
        } else if (greeting?.sent) {
          toast.success("Ticket closed ✓ — thank-you message sent");
        } else if (greeting && greeting.error) {
          toast.success("Ticket closed ✓", {
            description: "Auto-greeting not sent (customer may be outside 24h window)",
          });
        } else {
          toast.success("Ticket closed ✓");
        }
      } else if (status === "in_progress") {
        toast.success("Ticket re-opened");
      } else {
        toast.success(`Marked as ${status.replace("_", " ")}`);
      }
    } catch (_) {
      toast.error("Failed to update status");
    }
  };

  const closeTicket = async () => {
    if (!contact) return;
    if (!window.confirm(
      "Close this ticket?\n\nA feedback rating message will be automatically sent to the customer."
    )) return;
    try {
      // SINGLE atomic call — the status endpoint commits state clearance
      // (status=resolved, call_request=False, assigned_to=None, closed_at)
      // and broadcasts SSE BEFORE firing the chat_close_feedback bot. This
      // ensures the call icon stops blinking, the chat moves to the
      // resolved queue, and the agent gets unassigned BEFORE the template
      // template lands on the customer's WhatsApp. Earlier this was a
      // 2-step sequence (send-feedback → status) which caused the template
      // to be sent while the chat was still open, the call icon blinking,
      // and the agent still assigned — exactly what the user reported.
      await api.patch(`/conversations/${contact.phone}/status`, {
        status: "resolved",
        silent: true,
        send_feedback: true,
      });
      toast.success("Ticket closed ✓ — feedback message sent");
    } catch (_) {
      toast.error("Failed to close ticket");
    }
  };

  const sendFeedbackTemplate = async () => {
    if (!contact) return;
    if (!window.confirm(
      'Send the "How was your chat experience?" rating template to the customer now?',
    )) return;
    try {
      await api.post(`/conversations/${contact.phone}/send-feedback`);
      toast.success("Feedback template sent ✓");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to send feedback";
      toast.error(msg);
    }
  };
  const reopenTicket = () => updateStatus("in_progress");

  const resolveComplaint = async () => {
    try {
      const { data } = await api.patch(`/conversations/${contact.phone}/complaint/resolve`);
      if (data?.resolved) {
        toast.success("Complaint marked as resolved");
      } else {
        toast.message("No active complaint on this chat");
      }
    } catch (_) {
      toast.error("Failed to resolve complaint");
    }
  };

  const assign = async (user_id) => {
    try {
      await api.patch(`/conversations/${contact.phone}/assign`, { user_id: user_id || null });
      toast.success(user_id ? "Assigned" : "Unassigned");
    } catch (_) {
      toast.error("Failed to assign");
    }
  };

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("wh_token");
      const res = await fetch(`${API_BASE}/conversations/${contact.phone}/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `conversation_${contact.phone}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (_) {
      toast.error("Export failed");
    }
  };

  const addTemplate = async () => {
    const t = text.trim();
    if (!t) return toast.error("Type a message first");
    const title = t.slice(0, 40);
    try {
      const r = await api.post("/templates", { title, text: t });
      setTemplates((prev) => [...prev, r.data]);
      toast.success("Template saved");
    } catch (_) {
      toast.error("Failed to save template");
    }
  };

  const deleteTemplate = async (id) => {
    try {
      await api.delete(`/templates/${id}`);
      setTemplates((prev) => prev.filter((t) => t.id !== id));
    } catch (_) {
      toast.error("Delete failed");
    }
  };

  if (!contact) {
    return (
      <div className={`flex-1 ${mobileVisible ? "flex" : "hidden md:flex"}`}>
        <EmptyState />
      </div>
    );
  }

  const STATUS_LABEL = { new: "New", in_progress: "In Progress", resolved: "Resolved" };
  const STATUS_COLOR = {
    new: "bg-[#FEF3C7] text-[#92400E] hover:bg-[#FDE68A]",
    in_progress: "bg-[#DBEAFE] text-[#1E40AF] hover:bg-[#BFDBFE]",
    resolved: "bg-[#D1FAE5] text-[#065F46] hover:bg-[#A7F3D0]",
  };

  // Other agents currently viewing this chat (filter self out)
  const otherViewers = (viewers || []).filter(
    (v) => !me?.id || v.user_id !== me.id,
  );
  const claimedByOther = claim && me?.id && claim.user_id !== me.id;
  const ownClaim = claim && me?.id && claim.user_id === me.id;

  return (
    <section
      className={`flex-1 flex-col h-full bg-[var(--bg)] ${mobileVisible ? "flex" : "hidden md:flex"}`}
      data-testid="chat-window"
    >
      {/* Complaint banner — appears at the very top when an active complaint is flagged */}
      {contact.complaint && contact.complaint.active && (
        <div
          className="px-3 md:px-5 py-2.5 bg-[#DC2626] text-white flex items-center gap-3 border-b border-[#991B1B]"
          data-testid="complaint-banner"
        >
          <span className="h-2.5 w-2.5 rounded-full bg-white complaint-dot shrink-0" />
          <Siren size={18} weight="fill" className="shrink-0" />
          <div className="flex-1 min-w-0 text-sm">
            <div className="font-semibold truncate">
              Complaint raised by {contact.name || "+" + contact.phone}
            </div>
            <div className="text-[11px] opacity-90 truncate">
              Trigger: “{contact.complaint.trigger || contact.complaint.matched || "keyword"}”
              {contact.complaint.reason ? ` · via ${String(contact.complaint.reason).replace("_", " ")}` : ""}
            </div>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={resolveComplaint}
            className="h-8 gap-1.5 text-xs bg-white text-[#B91C1C] border-white hover:bg-[#FEE2E2]"
            data-testid="resolve-complaint-btn"
            title="Mark complaint as resolved"
          >
            <CheckCircle size={14} weight="fill" />
            Mark Resolved
          </Button>
        </div>
      )}
      {/* Claim-lock banner — warns if another agent currently owns this chat */}
      {claimedByOther && (
        <div
          className="px-3 md:px-5 py-2 bg-[#FEF3C7] dark:bg-[#78350F]/40 text-[#78350F] dark:text-[#FDE68A] flex items-center gap-3 border-b border-[#F59E0B]/40"
          data-testid="claim-banner"
        >
          <Lock size={16} weight="fill" className="shrink-0 text-[#B45309]" />
          <div className="flex-1 min-w-0 text-xs">
            <span className="font-semibold">{claim.user_name}</span> is handling this chat. Don't reply unless you want to take over.
          </div>
        </div>
      )}
      {ownClaim && (
        <div
          className="px-3 md:px-5 py-1.5 bg-[#D1FAE5] dark:bg-[#065F46]/40 text-[#065F46] dark:text-[#A7F3D0] flex items-center gap-2 border-b border-[#10B981]/30 text-[11px]"
          data-testid="own-claim-banner"
        >
          <Lock size={12} weight="fill" />
          You have claimed this chat. Other agents see a warning before opening.
        </div>
      )}
      {/* Header */}
      <div className="h-16 px-2 md:px-3 lg:px-5 border-b border-[var(--border)] bg-[var(--surface)] flex items-center gap-1 md:gap-1.5 lg:gap-2">
        {onBack && (
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden h-8 w-8 shrink-0 -ml-1"
            onClick={onBack}
            title="Back"
            data-testid="chat-back-btn"
          >
            <CaretLeft size={18} weight="bold" />
          </Button>
        )}
        <Avatar className="h-9 w-9 md:h-10 md:w-10 shrink-0">
          <AvatarFallback className="bg-[var(--fg)] text-[var(--bg)] text-xs">
            {initials(
              (contact.rodbez_name_verified && contact.rodbez_customer_name) || contact.name,
              contact.phone,
            )}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[15px] heading truncate flex items-center gap-1.5">
            {(() => { const ch = channelMeta(contact); return ch.key !== "whatsapp" && (
              <span
                className={`text-[8px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded shrink-0 text-white ${ch.badgeBg}`}
                title={ch.label}
                data-testid={`chat-header-channel-${ch.key}`}
              >
                {ch.iconText}
              </span>
            ); })()}
            {(() => {
              const fullName = (contact.rodbez_name_verified && contact.rodbez_customer_name)
                ? contact.rodbez_customer_name
                : contact.name;
              const trimmed = (fullName || "").trim();
              const firstName = trimmed.split(/\s+/)[0];
              const hasFull = trimmed.length > 0;
              const hasMore = trimmed.includes(" ");
              return (
                <>
                  <span className="truncate" data-testid="chat-header-first-name">
                    {hasFull ? firstName : contact.phone}
                  </span>
                  {hasFull && hasMore && (
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[var(--muted)] hover:text-[var(--fg)] hover:bg-[var(--bg-soft)] shrink-0 focus:outline-none focus:ring-2 focus:ring-[var(--fg)]/30"
                          data-testid="chat-header-full-name-btn"
                          title="View full name"
                          aria-label="View full name"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Info size={13} weight="bold" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent
                        side="bottom"
                        align="start"
                        className="w-auto max-w-xs p-3"
                        data-testid="chat-header-full-name-popover"
                      >
                        <div className="text-[11px] uppercase tracking-wide text-[var(--muted)] mb-1">
                          Full name
                        </div>
                        <div className="font-semibold text-sm break-words">
                          {trimmed}
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}
                </>
              );
            })()}
            {contact.rodbez_name_verified && (
              <span
                className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-[#10B981] text-white shrink-0"
                data-testid="chat-header-verified-badge"
                title={`Verified RodBez customer${contact.rodbez_customer_name ? `: ${contact.rodbez_customer_name}` : ""}`}
              >
                <CheckCircle size={12} weight="fill" />
              </span>
            )}
          </div>
          <div className="text-xs text-[var(--muted)] font-mono flex items-center gap-2 flex-wrap">
            {(() => { const ch = channelMeta(contact);
              return ch.key === "whatsapp"
                ? <span className="flex items-center gap-1"><Phone size={12} /> +{contact.phone}</span>
                : <span className="flex items-center gap-1 italic">{ch.addressLabel}</span>;
            })()}
            {otherViewers.length > 0 && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#065F46] bg-[#D1FAE5] dark:text-[#A7F3D0] dark:bg-[#064E3B]/50 px-1.5 py-0.5 rounded-full max-w-[140px]"
                data-testid="chat-viewers"
                title={otherViewers.map((v) => v.user_name).join(", ") + " also viewing this chat"}
              >
                <span className="relative flex h-1.5 w-1.5 shrink-0">
                  <span className="absolute inset-0 rounded-full bg-[#10B981] opacity-75 animate-ping" />
                  <span className="relative h-1.5 w-1.5 rounded-full bg-[#10B981]" />
                </span>
                <span className="truncate">
                  {otherViewers.length === 1
                    ? `${otherViewers[0].user_name.split(" ")[0]} viewing`
                    : `${otherViewers[0].user_name.split(" ")[0]} +${otherViewers.length - 1}`}
                </span>
              </span>
            )}
          </div>
        </div>

        {/* Chat activity (i) — show who replied + assignment timeline */}
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 shrink-0 text-[var(--muted)] hover:text-[var(--fg)]"
          onClick={() => setActivityOpen(true)}
          title="Chat activity (replies & assignments)"
          data-testid="chat-activity-btn"
        >
          <Info size={16} weight="duotone" />
        </Button>

        {/* Primary action cluster — Booking / Call / Feedback. Per user
            rule 2026-02-15 — moved from far-right to left-of-center so
            on tablet/narrow screens these critical buttons never get
            clipped. Labels still hide below `lg` (≥1024px) — at smaller
            widths only icons show. */}
        {onCreateBooking && (
          <Button
            variant="default"
            size="sm"
            className="h-8 gap-1.5 bg-[#25D366] hover:bg-[#1da851] text-white px-2 md:px-3 shrink-0"
            onClick={() => onCreateBooking(contact)}
            title="Create booking for this customer"
            data-testid="create-booking-btn"
          >
            <ClipboardText size={14} weight="fill" /> <span className="hidden lg:inline">Booking</span>
          </Button>
        )}

        <Button
          variant="default"
          size="sm"
          className="h-8 gap-1.5 bg-[#DC2626] hover:bg-[#B91C1C] text-white px-2 md:px-3 shrink-0"
          onClick={async () => {
            if (!contact?.phone) return;
            const ok = window.confirm(
              `Tata se +${contact.phone} pe call lagayi jaayegi.\n\nPehle aapke registered phone pe ring aayega — uthane par customer se bridge ho jayega.\n\nContinue?`
            );
            if (!ok) return;
            try {
              const r = await api.post("/tata/click-to-call", {
                phone: contact.phone,
              });
              toast.success("Call placed — aapke phone pe ring aayegi", {
                description: r?.data?.uuid ? `Call ID: ${r.data.uuid}` : null,
              });
            } catch (e) {
              const msg =
                e?.response?.data?.detail ||
                e?.message ||
                "Tata call failed";
              toast.error("Call failed", { description: msg });
            }
          }}
          title="Call this customer via Tata Smartflo"
          data-testid="tata-click-to-call-btn"
        >
          <Phone size={14} weight="fill" /> <span className="hidden lg:inline">Call</span>
        </Button>

        {me?.role === "admin" && (
          <Button
            variant="default"
            size="sm"
            className="h-8 gap-1.5 bg-[#7C3AED] hover:bg-[#6D28D9] text-white px-2 md:px-3 shrink-0"
            onClick={async () => {
              if (!contact?.phone) return;
              setMfOpen(true);
              setMfLoadingPreview(true);
              setMfPreview("");
              try {
                const r = await api.get(
                  `/templates/managment-feedback/preview?phone=${encodeURIComponent(contact.phone)}`
                );
                setMfParams(r?.data?.default_body_params || ["", ""]);
                setMfLabels(r?.data?.variable_labels || ["Your name", "Your designation"]);
                setMfPreview(r?.data?.preview_text || "");
              } catch (e) {
                setMfParams(["", ""]);
                setMfLabels(["Your name", "Your designation"]);
                setMfPreview("");
              } finally {
                setMfLoadingPreview(false);
              }
            }}
            title="Send management feedback template"
            data-testid="managment-feedback-btn"
          >
            <Megaphone size={14} weight="fill" />
            <span className="hidden lg:inline">Feedback</span>
          </Button>
        )}

        {/* Pushes the admin-cluster (status pill, bot, assign, export)
            to the far right while the action buttons above sit next to
            the customer name. */}
        <div className="ml-auto flex items-center gap-1 md:gap-1.5 lg:gap-2 min-w-0">

        {/* In-chat search — hidden until xl so the right-side action
            cluster (Close/Feedback/Bot/Assign/Book) gets full breathing
            room on smaller desktops. */}
        <div className="relative hidden xl:block">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
          <Input
            value={inChatQuery}
            onChange={(e) => setInChatQuery(e.target.value)}
            placeholder="Search in chat…"
            className="pl-8 h-8 w-[160px] bg-[var(--bg)] border-[var(--border)] text-xs"
            data-testid="in-chat-search"
          />
        </div>

        {/* Status pill */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={`h-8 px-3 rounded-full text-xs ${STATUS_COLOR[contact.status] || STATUS_COLOR.new}`}
              data-testid="status-pill"
            >
              {STATUS_LABEL[contact.status] || "New"}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Set status</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {Object.entries(STATUS_LABEL)
              // Per user rule 2026-02-11 — Resolved hata diya gaya hai
              // is dropdown se. Chat sirf "Close & Feedback" button se
              // close hogi (jo feedback template trigger karta hai +
              // proper audit trail leta hai). Agent chalaki se silent
              // close nahi kar paayega.
              .filter(([k]) => k !== "resolved")
              .map(([k, v]) => (
                <DropdownMenuItem
                  key={k}
                  onClick={() => updateStatus(k, { silent: false })}
                >
                  {v}
                </DropdownMenuItem>
              ))}
            <DropdownMenuSeparator />
            <div className="px-2 py-1.5 text-[10.5px] text-[var(--muted)] max-w-[200px] leading-tight">
              Chat close karne ke liye "Close &amp; Feedback" button use kariye.
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Close / Reopen ticket — any agent can close after talking to customer */}
        {contact.status === "resolved" ? (
          <Button
            variant="outline"
            size="sm"
            onClick={reopenTicket}
            className="h-8 gap-1.5 text-xs px-2 lg:px-3 border-[#FDE68A] text-[#92400E] hover:bg-[#FEF3C7]"
            data-testid="reopen-ticket-btn"
            title="Re-open this ticket"
          >
            <ArrowCounterClockwise size={14} weight="bold" />
            <span className="hidden lg:inline">Re-open</span>
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            onClick={closeTicket}
            className="h-8 gap-1.5 text-xs px-2 lg:px-3 border-[#A7F3D0] text-[#065F46] hover:bg-[#D1FAE5]"
            data-testid="close-ticket-btn"
            title="Close this ticket — feedback rating message will be sent automatically"
          >
            <CheckCircle size={14} weight="fill" />
            <span className="hidden lg:inline">Close & Feedback</span>
          </Button>
        )}

        {/* Bot controls — hidden on mobile (available via overflow menu) */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className={`hidden md:inline-flex h-8 gap-1.5 text-xs px-2 lg:px-3 ${
                botState?.is_paused
                  ? "text-[#92400E]"
                  : botState?.step
                  ? "text-[#065F46] border-[#A7F3D0]"
                  : ""
              }`}
              data-testid="bot-control-btn"
              title="Bot controls"
            >
              <Robot size={14} weight="fill" />
              <span className="hidden xl:inline">
                {botState?.is_paused
                  ? "Bot Paused"
                  : botState?.step
                  ? "Bot Active"
                  : "Bot"}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              Bot status
              <div className="text-[10px] font-normal text-[var(--muted)] mt-0.5">
                {botState?.is_paused
                  ? `Paused (customer ne type kiya to agent tak jayega)`
                  : botState?.step
                  ? `Active · step: ${botState.step}`
                  : "Idle"}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {botState?.is_paused ? (
              <DropdownMenuItem onClick={() => botPauseResume("resume")} data-testid="bot-resume">
                ▶ Resume bot
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={() => botPauseResume("pause")} data-testid="bot-pause">
                ⏸ Pause bot (24h)
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => botPauseResume("reset")} data-testid="bot-reset">
              ↺ Reset bot state
            </DropdownMenuItem>
            {botState?.last_booking_id && (
              <>
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5 text-[10px] text-[var(--muted)]">
                  Last booking: {botState.last_booking_id}
                </div>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Assign — hidden on mobile (available via overflow menu) */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="hidden md:inline-flex h-8 gap-1.5 text-xs px-2 lg:px-3" data-testid="assign-btn">
              <UserCircle size={14} />
              <span className="hidden xl:inline">
                {contact.assigned_to
                  ? (contact.assigned_to.full_name || contact.assigned_to.email || "").split("@")[0]
                  : "Assign"}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>Assign agent</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => assign(null)}>
              <X size={14} className="mr-2" /> Unassigned
            </DropdownMenuItem>
            {users.map((u) => (
              <DropdownMenuItem key={u.id} onClick={() => assign(u.id)}>
                {u.full_name || u.email}
                <span className="ml-auto text-[10px] text-[var(--muted)]">{u.role}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Button variant="ghost" size="icon" className="hidden md:inline-flex h-8 w-8" onClick={exportCsv} title="Export CSV" data-testid="export-csv">
          <DownloadSimple size={16} />
        </Button>

        {/* Mobile overflow menu — Bot controls, Assign, Export */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              className="md:hidden h-8 w-8 shrink-0"
              data-testid="chat-overflow-btn"
              title="More actions"
            >
              <DotsThree size={18} weight="bold" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56" data-testid="chat-overflow-content">
            <DropdownMenuLabel>
              Bot
              <div className="text-[10px] font-normal text-[var(--muted)] mt-0.5">
                {botState?.is_paused
                  ? "Paused"
                  : botState?.step
                  ? `Active · ${botState.step}`
                  : "Idle"}
              </div>
            </DropdownMenuLabel>
            {botState?.is_paused ? (
              <DropdownMenuItem onClick={() => botPauseResume("resume")} data-testid="bot-resume-mobile">
                ▶ Resume bot
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={() => botPauseResume("pause")} data-testid="bot-pause-mobile">
                ⏸ Pause bot (24h)
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => botPauseResume("reset")} data-testid="bot-reset-mobile">
              ↺ Reset bot state
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Assign</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => assign(null)} data-testid="assign-none-mobile">
              <X size={14} className="mr-2" /> Unassigned
            </DropdownMenuItem>
            {users.slice(0, 8).map((u) => (
              <DropdownMenuItem key={u.id} onClick={() => assign(u.id)}>
                {u.full_name || u.email}
                <span className="ml-auto text-[10px] text-[var(--muted)]">{u.role}</span>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={exportCsv} data-testid="export-csv-mobile">
              <DownloadSimple size={14} className="mr-2" /> Export chat CSV
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* Booking / Call / Feedback action cluster moved to the LEFT
            of the header (right after avatar + name + activity icon)
            per user rule 2026-02-15. Old right-aligned block removed. */}
        </div>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto scroll-thin px-3 md:px-6 py-4 md:py-5"
      >
        <div className="mx-auto max-w-2xl flex flex-col gap-3">
          {/* Unique RodBez-themed initial loader — shows when a chat is
              opened but messages haven't arrived yet. */}
          {initialLoading && messages.length === 0 && (
            <div
              className="flex flex-col items-center justify-center py-16 select-none"
              data-testid="chat-initial-loader"
            >
              <div className="relative">
                {/* Spinning ring of dots — unique 8-dot pulse, RodBez green/gold palette */}
                <div className="w-20 h-20 relative">
                  {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
                    <span
                      key={i}
                      className="absolute top-1/2 left-1/2 w-2 h-2 rounded-full"
                      style={{
                        background: i % 2 === 0 ? "#25D366" : "#FBBF24",
                        transform: `translate(-50%, -50%) rotate(${i * 45}deg) translateY(-30px)`,
                        animation: `rb-loader-pulse 1s ease-in-out ${i * 0.12}s infinite`,
                      }}
                    />
                  ))}
                </div>
                {/* Center logo monogram */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center font-extrabold text-[11px] tracking-tight"
                    style={{
                      background: "#0F172A",
                      color: "#FFF",
                      boxShadow: "0 0 20px rgba(37, 211, 102, 0.35)",
                    }}
                  >
                    R<span style={{ color: "#FBBF24" }}>B</span>
                  </div>
                </div>
              </div>
              <div className="mt-5 text-[13px] font-medium text-[var(--fg)] flex items-center gap-1">
                Loading conversation
                <span className="inline-flex gap-0.5 ml-0.5">
                  <span className="rb-dot" />
                  <span className="rb-dot" style={{ animationDelay: "0.18s" }} />
                  <span className="rb-dot" style={{ animationDelay: "0.36s" }} />
                </span>
              </div>
              <div className="mt-1 text-[11px] text-[var(--muted)]">
                Fetching last 30 messages from RodBez inbox…
              </div>
              <style>{`
                @keyframes rb-loader-pulse {
                  0%, 100% { opacity: 0.25; transform: translate(-50%, -50%) rotate(var(--r, 0deg)) translateY(-30px) scale(0.85); }
                  50%      { opacity: 1;    transform: translate(-50%, -50%) rotate(var(--r, 0deg)) translateY(-30px) scale(1.15); }
                }
                @keyframes rb-dot-bounce {
                  0%, 80%, 100% { opacity: 0.3; transform: translateY(0); }
                  40%           { opacity: 1;   transform: translateY(-3px); }
                }
                .rb-dot {
                  display: inline-block;
                  width: 4px; height: 4px;
                  border-radius: 999px;
                  background: currentColor;
                  animation: rb-dot-bounce 1.2s infinite ease-in-out;
                }
              `}</style>
            </div>
          )}

          {/* Top-of-list pagination indicator */}
          {messages.length > 0 && (olderLoading || olderHasMore) && (
            <div
              className="text-[11px] text-[var(--muted)] text-center py-1 flex items-center justify-center gap-1.5"
              data-testid="messages-load-older"
            >
              {olderLoading ? (
                <>
                  <CircleNotch size={12} className="animate-spin" />
                  Loading older messages…
                </>
              ) : (
                <span className="opacity-70">Scroll up to load older messages</span>
              )}
            </div>
          )}
          {messages.length > 0 && !olderHasMore && !olderLoading && (
            <div className="text-[11px] text-[var(--muted)] text-center py-1 opacity-60">
              — Beginning of conversation —
            </div>
          )}
          {inChatQuery && (
            <div className="text-[11px] text-[var(--muted)] text-center">
              Showing {filteredMessages.length} of {messages.length} matching "{inChatQuery}"
            </div>
          )}
          <AnimatePresence initial={false}>
            {/* Quick lookup of wamid → message for rendering quoted-reply
                previews. Per user rule 2026-02-13 — reply-to-message UI. */}
            {(() => null)()}
            {filteredMessages.map((m) => {
              const outgoing = m.direction === "outgoing";
              const quoted = m.replied_to
                ? messages.find((x) => x.id === m.replied_to)
                : null;
              return (
                <motion.div
                  key={m.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.18 }}
                  className={`flex ${outgoing ? "justify-end" : "justify-start"}`}
                  data-testid={`message-${m.id}`}
                >
                  <div className="group relative max-w-[80%]">
                    <div
                      className={`px-4 py-2 shadow-sm ${
                        outgoing
                          ? "bg-[var(--fg)] text-[var(--bg)] rounded-2xl rounded-tr-sm"
                          : "bg-[var(--surface)] border border-[var(--border)] text-[var(--fg)] rounded-2xl rounded-tl-sm"
                      }`}
                    >
                      {/* Quoted-reply preview (WhatsApp-style) — shows the
                          message being replied to as a small bordered
                          block above the main text. Per user rule
                          2026-02-13. Click jumps to the original
                          message. */}
                      {m.replied_to && (
                        <button
                          type="button"
                          className={`block w-full text-left mb-1.5 px-2 py-1 rounded-md border-l-2 ${
                            outgoing
                              ? "bg-[var(--bg)]/15 border-[var(--bg)]/60 hover:bg-[var(--bg)]/25"
                              : "bg-[var(--bg)] border-[#25D366] hover:bg-[var(--bg)]/80"
                          } transition-colors`}
                          onClick={() => {
                            const node = document.querySelector(
                              `[data-testid="message-${m.replied_to}"]`
                            );
                            if (node) {
                              node.scrollIntoView({ behavior: "smooth", block: "center" });
                              node.classList.add("ring-2", "ring-[#25D366]");
                              setTimeout(() => {
                                node.classList.remove("ring-2", "ring-[#25D366]");
                              }, 1500);
                            }
                          }}
                          title="Jump to quoted message"
                          data-testid={`quoted-preview-${m.id}`}
                        >
                          <div className={`text-[10px] font-semibold mb-0.5 ${
                            outgoing ? "text-[var(--bg)]/80" : "text-[#25D366]"
                          }`}>
                            {quoted
                              ? (quoted.direction === "outgoing"
                                  ? (quoted.sent_by?.full_name || "You")
                                  : (contact?.name || "Customer"))
                              : "Original message"}
                          </div>
                          <div className={`text-[11.5px] line-clamp-2 ${
                            outgoing ? "text-[var(--bg)]/85" : "text-[var(--muted)]"
                          }`}>
                            {quoted?.text || quoted?.media?.kind
                              ? (quoted?.text || `[${quoted?.media?.kind || "media"}]`)
                              : "Message not loaded"}
                          </div>
                        </button>
                      )}
                      {(m.media || m.location) && (
                        <MediaBubble message={m} outgoing={outgoing} />
                      )}
                      {!m.media && !m.location && (
                        <div className="text-sm leading-relaxed whitespace-pre-wrap break-words">
                          {m.template_meta?.header && (
                            <div className={`font-bold mb-1 ${outgoing ? "text-[var(--bg)]" : ""}`}>
                              {m.template_meta.header}
                            </div>
                          )}
                          {m.text || <em className="opacity-70">[empty]</em>}
                          {m.template_meta?.footer && (
                            <div className={`text-[11px] mt-1.5 ${outgoing ? "text-[var(--bg)]/60" : "text-[var(--muted)]"}`}>
                              {m.template_meta.footer}
                            </div>
                          )}
                          {m.type === "template" && m.template_meta?.name && (
                            <div className={`mt-2 inline-flex items-center gap-1 text-[10px] tracking-wider uppercase px-2 py-0.5 rounded-full ${
                              outgoing ? "bg-[#25D366]/25 text-[#b6ffce]" : "bg-[#25D366]/10 text-[#0f7a3c]"
                            }`}>
                              <span className="h-1 w-1 rounded-full bg-current" /> Template · {m.template_meta.name}
                            </div>
                          )}
                          {/* Inline "Translate" — only on INBOUND text bubbles
                              with non-empty text. Shows Hindi translation
                              below original on click; cached per message id. */}
                          {!outgoing && m.text && m.text.trim() && (
                            <div className="mt-1.5">
                              {!translations[m.id] ? (
                                <button
                                  type="button"
                                  className="text-[11px] underline opacity-70 hover:opacity-100 transition-opacity"
                                  onClick={() => translateMessage(m.id, m.text)}
                                  data-testid={`translate-msg-${m.id}`}
                                >
                                  🌐 Hindi me translate karein
                                </button>
                              ) : translations[m.id].loading ? (
                                <div className="text-[11px] opacity-70 italic">Translating…</div>
                              ) : translations[m.id].error ? (
                                <div className="text-[11px] text-red-500">
                                  {translations[m.id].error}
                                </div>
                              ) : (
                                <div className="mt-1 pt-1.5 border-t border-current/15 text-[12px] italic opacity-90 leading-snug">
                                  ↳ {translations[m.id].translated}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      <div
                        className={`text-[10px] mt-1 flex items-center gap-1 ${
                          outgoing ? "justify-end text-[var(--bg)]/60" : "text-[var(--muted)]"
                        }`}
                      >
                        <span>{formatISTDateTime(m.timestamp)}</span>
                        {outgoing && <Tick status={m.status} failure={m.failure} />}
                      </div>
                      {m.sent_by && outgoing && (
                        <div
                          className="text-[10px] mt-0.5 flex items-center gap-1 justify-end font-medium text-[var(--bg)]/85"
                          data-testid={`replied-by-${m.id}`}
                        >
                          <UserCircle size={11} weight="fill" />
                          <span>Replied by {m.sent_by.full_name || (m.sent_by.email || "").split("@")[0]}</span>
                        </div>
                      )}
                    </div>

                    {/* Reactions on the message (displayed below bubble) */}
                    {m.reactions && Object.values(m.reactions).length > 0 && (
                      <div
                        className={`mt-1 flex flex-wrap gap-1 ${
                          outgoing ? "justify-end" : "justify-start"
                        }`}
                      >
                        {Object.values(m.reactions).map((e, idx) => (
                          <span
                            key={idx}
                            className="text-xs px-1.5 py-0.5 rounded-full bg-[var(--surface)] border border-[var(--border)] shadow-sm"
                          >
                            {e}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Hover action group: Reply + Reaction picker.
                        Per user rule 2026-02-13 — agents can quote any
                        message by clicking the curved-arrow icon. */}
                    <div
                      className={`absolute top-0 ${
                        outgoing ? "-left-[78px]" : "-right-[78px]"
                      } flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity`}
                    >
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 rounded-full bg-[var(--surface)] border border-[var(--border)] shadow-sm"
                        title="Reply"
                        onClick={() => {
                          setReplyingTo(m);
                          // Focus the composer after a tick so the user
                          // can start typing immediately.
                          setTimeout(() => inputRef.current?.focus?.(), 60);
                        }}
                        data-testid={`reply-to-msg-${m.id}`}
                      >
                        <ArrowBendUpLeft size={14} />
                      </Button>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 rounded-full bg-[var(--surface)] border border-[var(--border)] shadow-sm"
                            title="React"
                          >
                            <Smiley size={14} />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="w-auto p-1 flex gap-1">
                          {["❤️", "👍", "👎", "😂", "😮", "🙏"].map((e) => (
                            <button
                              key={e}
                              type="button"
                              onClick={() => react(m.id, e)}
                              className="text-xl hover:scale-125 transition-transform p-1"
                            >
                              {e}
                            </button>
                          ))}
                        <button
                          type="button"
                          onClick={() => react(m.id, "")}
                          className="text-xs px-2 opacity-60"
                          title="Remove"
                        >
                          ✕
                        </button>
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
          <div ref={endRef} />
        </div>
      </div>

      {/* Composer */}
      <div className="px-3 md:px-5 py-3 border-t border-[var(--border)] bg-[var(--surface)] relative">
        {/* Bot-handover gate — when the bot is actively driving the chat,
            agents must explicitly take over before sending a message. This
            prevents accidental mid-flow collisions (e.g. customer is in step
            "nb.pickup" and agent fires off a template that confuses the flow).

            Per user rule 2026-02-14 — the banner now ALSO hides when:
              • The ticket is resolved (bot is done; chat silently closed).
              • More than 5 minutes have passed since the last bot reply OR
                customer message — the bot has effectively gone idle, the
                chat is treated as closed from the agent's perspective.
            The banner re-appears organically when the customer messages
            again (loadBotState refreshes `last_active`). */}
        {(() => {
          if (!botState?.step || botState?.is_paused) return false;
          // Resolved ticket → bot is done, nothing for agent to take over.
          if (contact?.status === "resolved") return false;
          // 5-min idle gate. Use the most-recent of bot's last_active
          // OR customer's last inbound message timestamp.
          const FIVE_MIN_MS = 5 * 60 * 1000;
          const now = Date.now();
          const botMs = botState?.last_active
            ? Date.parse(botState.last_active)
            : 0;
          const lastMs = contact?.last_timestamp
            ? Date.parse(contact.last_timestamp)
            : 0;
          const latest = Math.max(botMs || 0, lastMs || 0);
          if (!latest) return false;  // no signal → don't show
          // `tick` referenced to ensure re-render uses fresh `now`.
          // eslint-disable-next-line no-unused-expressions
          tick;
          if (now - latest > FIVE_MIN_MS) return false;
          return true;
        })() && (
          <div
            className="absolute inset-0 z-20 flex items-center justify-center bg-[var(--surface)]/95 backdrop-blur-sm"
            data-testid="bot-handover-gate"
          >
            <div className="flex items-center gap-3 px-4 py-3 rounded-lg border border-[#F59E0B] bg-[#FEF3C7] text-[#78350F] dark:bg-[#78350F]/30 dark:text-[#FCD34D] dark:border-[#F59E0B]/60 shadow-md max-w-full mx-3">
              <div className="text-lg shrink-0">🤖</div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold leading-tight">
                  Bot abhi customer ko handle kar raha hai
                </div>
                <div className="text-[11px] opacity-90 leading-tight mt-0.5">
                  Current step: <span className="font-mono">{botState.step}</span> — agar aap takeover karenge to bot 24 ghante ke liye paused ho jayega.
                </div>
              </div>
              <Button
                type="button"
                size="sm"
                className="h-8 gap-1.5 bg-[#F59E0B] hover:bg-[#D97706] text-white shrink-0"
                onClick={async () => {
                  await botPauseResume("pause");
                  toast.info("Bot paused — ab aap reply kar sakte hain");
                }}
                data-testid="bot-takeover-btn"
              >
                Takeover from bot
              </Button>
            </div>
          </div>
        )}
        {/* Live activity indicator — surface when ANOTHER agent has the
            same chat open or is typing right now. Mirrors the green pill
            shown in the sidebar so the agent in detail view also gets a
            clear "don't double-handle" signal (per user 2026-02). */}
        {(() => {
          const meId = me?.id;
          const otherViewers = (viewers || []).filter(
            (v) => v && v.user_id && v.user_id !== meId,
          );
          // Dedupe with claim holder so single agent never appears twice
          const claimHolderId = claim?.user_id;
          const claimedByOther = claimHolderId && claimHolderId !== meId;
          const viewersDedup = claimedByOther
            ? otherViewers.filter((v) => v.user_id !== claimHolderId)
            : otherViewers;
          const otherTyper =
            othersTyping
            && othersTyping.user_id
            && othersTyping.user_id !== meId
              ? othersTyping
              : null;
          const claimHolderName = claim?.user_name
            || (claim?.user_email || "").split("@")[0];
          const showStrip =
            !!otherTyper || viewersDedup.length > 0 || claimedByOther;
          if (!showStrip) return null;
          return (
            <div
              className="mb-2 flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#10B981]/40 bg-[#10B981]/10 text-[#065F46] dark:text-[#6EE7B7] text-[12px]"
              data-testid="other-agents-strip"
            >
              {otherTyper ? (
                <>
                  <PencilSimple size={14} weight="fill" className="shrink-0 animate-pulse" />
                  <div className="min-w-0 flex-1 leading-tight truncate">
                    <span className="font-semibold">
                      {(otherTyper.user_name || "Someone").split(" ")[0]}
                    </span>{" "}
                    typing — don't reply
                  </div>
                </>
              ) : (
                <>
                  <span className="relative flex h-2 w-2 shrink-0">
                    <span className="absolute inset-0 rounded-full bg-[#10B981] opacity-75 animate-ping" />
                    <span className="relative h-2 w-2 rounded-full bg-[#10B981]" />
                  </span>
                  <div className="min-w-0 flex-1 leading-tight truncate">
                    {claimedByOther && (
                      <>
                        <span className="font-semibold">
                          {(claimHolderName || "Someone").split(" ")[0]}
                        </span>{" "}
                        handling
                        {viewersDedup.length > 0 ? " · " : ""}
                      </>
                    )}
                    {viewersDedup.length > 0 && (
                      <>
                        <span className="font-semibold">
                          {viewersDedup
                            .map((v) => (v.user_name || "Someone").split(" ")[0])
                            .join(", ")}
                        </span>{" "}
                        viewing
                      </>
                    )}
                    <span className="opacity-80"> — don't reply</span>
                  </div>
                </>
              )}
            </div>
          );
        })()}
        {/* Greet customer first — REQUIRED gate. Until the agent sends a
            message containing "My name is …", all sends/uploads are blocked.
            One click on Send pre-fills + sends a time-aware greeting using
            the agent's first name. Hidden when bot-handover gate is active. */}
        {/* Soft Lock banner — another agent replied recently.
            Politeness gate before this agent takes over. */}
        {softLockInfo && (
          <div
            className="mx-3 mt-2 px-3 py-1.5 rounded-md flex items-center gap-2 text-xs bg-[#FEF3C7] dark:bg-[#78350F]/40 border border-[#F59E0B]/40 text-[#92400E] dark:text-[#FCD34D]"
            data-testid="soft-lock-banner"
          >
            <Warning size={14} weight="fill" className="text-[#F59E0B] shrink-0" />
            <div className="flex-1 min-w-0 truncate">
              <span className="font-semibold">{softLockInfo.agentName}</span>{" "}
              replied {softLockInfo.minutesAgo}m ago
              {takeoverConfirmed ? (
                <span className="ml-1 font-medium text-[#10B981] dark:text-[#34D399]">
                  · taken over
                </span>
              ) : (
                <span className="ml-1 opacity-80">— take over?</span>
              )}
            </div>
            {!takeoverConfirmed && (
              <button
                onClick={() => setTakeoverConfirmed(true)}
                className="px-2 py-1 rounded bg-[#F59E0B] hover:bg-[#D97706] text-white text-[11px] font-semibold shrink-0"
                data-testid="soft-lock-takeover-btn"
              >
                Take over
              </button>
            )}
          </div>
        )}

        {mustGreetFirst && (() => {
          const hour = new Date().getHours();
          const tod =
            hour >= 5 && hour < 12
              ? "Good Morning ☀️"
              : hour >= 12 && hour < 17
                ? "Good Afternoon 🌤️"
                : hour >= 17 && hour < 21
                  ? "Good Evening 🌆"
                  : "Hello 🌙";
          const agentFullName = (me.full_name || me.email || "").trim();
          const agentFirstName = agentFullName.split(/[\s@]/)[0] || "Admin";
          // Customer name preference:
          //  1) RodBez API name (cached on contact via /external/customer/find)
          //  2) WhatsApp profile name
          //  3) generic "there"
          const rodbezName =
            (contact?.rodbez_customer_name || "").trim();
          const waName = (contact?.name || "").trim();
          // Treat "name == phone" as no real name
          const waNameClean =
            waName && waName !== contact?.phone ? waName : "";
          const customerName =
            rodbezName || waNameClean || "there";
          // If the first word is too short to feel like a real name (e.g. "Lk"
          // from "Lk Thakur"), fall back to the full name so the greeting
          // doesn't look broken.
          const firstWord = customerName.split(/\s+/)[0];
          const customerFirst =
            firstWord.length < 4 && customerName.includes(" ")
              ? customerName
              : firstWord;
          const greetText =
            `Hello ${customerFirst}, ${tod} My name is ${agentFirstName}. ` +
            `It’s a pleasure to assist you today. How can I help you with your *RodBez* journey?`;
          return (
            // Compact greet pill — single tap fills the composer with
            // the personalised greeting. Replaces the previous bulky
            // banner (which showed full preview + Fill + Send buttons)
            // because on tablet width that ate too much vertical space
            // and the action buttons were getting clipped. Per user
            // rule 2026-02-15 — keep this minimal, just a button.
            <button
              type="button"
              onClick={() => {
                setText(greetText);
                // focus the composer so the agent can review / edit
                setTimeout(() => inputRef.current?.focus(), 0);
              }}
              title={`Click to fill: ${greetText}`}
              className="mb-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[#F59E0B]/60 bg-[#F59E0B]/10 hover:bg-[#F59E0B]/20 text-[#92400E] dark:text-[#FCD34D] text-[12px] font-semibold transition-colors self-start"
              data-testid="greet-fill-btn"
            >
              <span className="text-sm">👋</span>
              Greet karein
              <span className="opacity-70 font-normal text-[11px]">(zaroori)</span>
            </button>
          );
        })()}
        <form
          className="flex items-center gap-1.5 md:gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
          data-testid="chat-composer"
        >
          {/* File upload (images, PDFs, etc.) */}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            accept="image/*,video/*,audio/*,application/pdf"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) uploadAndSend(f);
              e.target.value = "";
            }}
            data-testid="chat-file-input"
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0"
            title="Attach image or PDF"
            disabled={uploading}
            onClick={() => fileInputRef.current?.click()}
            data-testid="attach-btn"
          >
            {uploading ? <CircleNotch size={16} className="animate-spin" /> : <Paperclip size={16} />}
          </Button>
          {/* Templates popover */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-9 w-9 shrink-0"
                title="Quick replies"
                data-testid="templates-btn"
              >
                <BookmarkSimple size={16} />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-80 p-0">
              <div className="p-3 border-b border-[var(--border)]">
                <div className="text-xs font-semibold heading">Quick replies</div>
                <div className="text-[11px] text-[var(--muted)] mt-0.5">
                  Click to insert, or save current draft as template.
                </div>
              </div>
              <div className="max-h-64 overflow-y-auto scroll-thin">
                {templates.length === 0 ? (
                  <div className="p-4 text-xs text-[var(--muted)]">No templates yet.</div>
                ) : (
                  <ul>
                    {templates.map((t) => (
                      <li key={t.id} className="border-b border-[var(--border)] last:border-0">
                        <div className="p-3 flex items-start gap-2 hover:bg-[var(--bg)]">
                          <button
                            type="button"
                            onClick={() => setText(t.text)}
                            className="flex-1 text-left"
                            data-testid={`template-${t.id}`}
                          >
                            <div className="text-xs font-medium">{t.title}</div>
                            <div className="text-[11px] text-[var(--muted)] line-clamp-2 mt-0.5">
                              {t.text}
                            </div>
                          </button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 shrink-0"
                            onClick={() => deleteTemplate(t.id)}
                          >
                            <Trash size={12} />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="p-2 border-t border-[var(--border)]">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-full gap-1.5 h-8 text-xs"
                  onClick={addTemplate}
                  data-testid="save-template-btn"
                >
                  <Plus size={12} /> Save current text as template
                </Button>
              </div>
            </PopoverContent>
          </Popover>

          {/* Book Now — one-click trigger for the RodBez booking bot.
              Useful when an agent wants the customer to see the cab-type
              menu without typing "Hi" themselves. Lifts pause + clears
              stale sessions before firing. */}
          {!isFb && !isIg && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 text-[#25D366] hover:bg-[#25D366]/10"
              title="Book Now — fire booking bot for this customer"
              disabled={triggeringBot || !contact?.phone}
              onClick={triggerBookingBot}
              data-testid="book-now-btn"
            >
              {triggeringBot ? (
                <CircleNotch size={16} className="animate-spin" />
              ) : (
                <Robot size={16} weight="fill" />
              )}
            </Button>
          )}

          <div className="relative flex-1 min-w-0">
            {/* Reply-to banner (per user rule 2026-02-13). Shows the
                message being quoted with a × close button. Sits above
                the textarea like WhatsApp. */}
            {replyingTo && (
              <div
                className="mb-1.5 px-2.5 py-1.5 rounded-md bg-[var(--bg)] border-l-2 border-[#25D366] flex items-start gap-2"
                data-testid="reply-compose-banner"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] font-semibold text-[#25D366] mb-0.5">
                    Replying to{" "}
                    {replyingTo.direction === "outgoing"
                      ? (replyingTo.sent_by?.full_name || "You")
                      : (contact?.name || "Customer")}
                  </div>
                  <div className="text-[11.5px] text-[var(--muted)] line-clamp-2 break-words">
                    {replyingTo.text || `[${replyingTo.media?.kind || "media"}]`}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setReplyingTo(null)}
                  className="text-[var(--muted)] hover:text-[var(--fg)] shrink-0 p-0.5"
                  title="Cancel reply"
                  data-testid="reply-compose-cancel"
                >
                  <X size={14} weight="bold" />
                </button>
              </div>
            )}
            {/* Shortcut autocomplete popup */}
            {ac.open && acMatches.length > 0 && (
              <div
                className="absolute bottom-full left-0 right-0 mb-1.5 max-h-64 overflow-y-auto rounded-md border border-[var(--border)] bg-[var(--card)] shadow-xl scroll-thin z-30"
                data-testid="shortcut-autocomplete"
              >
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-[var(--muted)] font-semibold border-b border-[var(--border)] flex items-center gap-1.5 bg-[var(--bg)]">
                  <Hash size={10} weight="bold" /> Shortcuts · ↑↓ navigate · Enter to insert · Esc to close
                </div>
                <ul>
                  {acMatches.map((sc, i) => (
                    <li
                      key={sc.code}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        insertShortcut(sc);
                      }}
                      onMouseEnter={() => setAc((a) => ({ ...a, index: i }))}
                      className={`px-3 py-2 cursor-pointer border-b border-[var(--border)] last:border-0 ${
                        i === ac.index ? "bg-[#25D366]/10" : ""
                      }`}
                      data-testid={`shortcut-suggest-${sc.code}`}
                    >
                      <div className="text-xs font-mono font-semibold text-[#25D366]">
                        {sc.code}
                      </div>
                      <div className="text-[11px] text-[var(--muted)] line-clamp-2 whitespace-pre-wrap mt-0.5">
                        {sc.reply}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <Textarea
              ref={inputRef}
              value={text}
              onChange={(e) => updateText(e.target.value, e.target.selectionStart)}
              onKeyDown={handleInputKeyDown}
              onClick={(e) => updateText(text, e.target.selectionStart)}
              onKeyUp={(e) => {
                // Only re-check token on cursor-moving keys (avoid duplicate work on typing)
                if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) {
                  updateText(text, e.target.selectionStart);
                }
              }}
              placeholder={
                mustGreetFirst
                  ? "Pehle customer ko greet karein… ('My name is …' include karein)"
                  : "Type a reply… (use #code for quick replies, Enter to send, Shift+Enter for new line)"
              }
              className={`min-h-[40px] max-h-32 bg-[var(--bg)] border-[var(--border)] resize-none py-2 ${
                mustGreetFirst ? "border-[#F59E0B]/60 focus-visible:ring-[#F59E0B]/40" : ""
              }`}
              rows={1}
              disabled={sending}
              data-testid="chat-message-input"
            />
          </div>
          {/* Top-row Send button: HIDDEN when auto-translate is ON (the translated
              row below carries its own Send button so the agent always sees the
              exact text that will go out). */}
          {!composerTranslate && (
            <Button
              type="submit"
              className="h-10 gap-1.5 shrink-0 md:min-w-[100px] px-3 md:px-4 bg-[var(--fg)] hover:opacity-90 text-[var(--bg)]"
              disabled={
                sending ||
                !text.trim() ||
                (mustGreetFirst && !isGreetingMessage(text))
              }
              title={
                mustGreetFirst && !isGreetingMessage(text)
                  ? "Pehle customer ko greet karein"
                  : undefined
              }
              data-testid="send-message-button"
            >
              {sending ? (
                <>
                  <CircleNotch size={14} className="animate-spin" /> <span className="hidden md:inline">Sending</span>
                </>
              ) : (
                <>
                  <PaperPlaneTilt size={14} weight="fill" /> <span className="hidden md:inline">Send</span>
                </>
              )}
            </Button>
          )}
        </form>
        {/* Auto-translate toggle */}
        <div className="mt-2">
          <label className="inline-flex items-center gap-1.5 text-[11px] text-[var(--muted)] cursor-pointer select-none">
            <input
              type="checkbox"
              checked={composerTranslate}
              onChange={(e) => setComposerTranslate(e.target.checked)}
              className="h-3.5 w-3.5 cursor-pointer accent-[#25D366]"
              data-testid="composer-translate-toggle"
            />
            <span className="font-medium">🌐 Send as English (Hindi/Hinglish type karein, English bhejega)</span>
          </label>
        </div>
        {/* Translated-output row WITH its own Send button — visible only when
            auto-translate is ON. This is the EXACT text the customer will get,
            so the Send button lives here for full visibility. */}
        {composerTranslate && (
          <div
            className="mt-2 flex items-stretch gap-1.5 md:gap-2"
            data-testid="composer-translation-row"
          >
            <div className="flex-1 min-w-0 px-3 py-2 rounded-md border-2 border-[#0EA5E9]/50 bg-[#0EA5E9]/5">
              <div className="text-[10px] uppercase tracking-wide text-[#0369A1] dark:text-[#7DD3FC] font-bold mb-0.5">
                {composerPreview.loading
                  ? "Translating to English…"
                  : composerPreview.translated
                    ? "Will be sent as (English):"
                    : "Will be sent as (English):"}
              </div>
              <div className="text-sm text-[var(--fg)] leading-snug whitespace-pre-wrap break-words min-h-[20px]">
                {composerPreview.loading ? (
                  <span className="opacity-60 italic">…</span>
                ) : composerPreview.translated ? (
                  composerPreview.translated
                ) : (
                  <span className="opacity-50 italic">
                    {text.trim()
                      ? "Translation pending…"
                      : "Type Hindi/Hinglish above"}
                  </span>
                )}
              </div>
            </div>
            <Button
              type="button"
              onClick={send}
              className="h-auto gap-1.5 md:min-w-[100px] px-3 md:px-4 bg-[var(--fg)] hover:opacity-90 text-[var(--bg)]"
              disabled={
                sending ||
                !text.trim() ||
                (composerPreview.loading && !composerPreview.translated) ||
                (mustGreetFirst && !isGreetingMessage(text))
              }
              title={
                mustGreetFirst && !isGreetingMessage(text)
                  ? "Pehle customer ko greet karein"
                  : undefined
              }
              data-testid="send-translated-button"
            >
              {sending ? (
                <>
                  <CircleNotch size={14} className="animate-spin" /> <span className="hidden md:inline">Sending</span>
                </>
              ) : (
                <>
                  <PaperPlaneTilt size={14} weight="fill" /> <span className="hidden md:inline">Send</span>
                </>
              )}
            </Button>
          </div>
        )}
        <div className="text-[11px] text-[var(--muted)] mt-1.5 hidden sm:block">
          WhatsApp 24h rule: free-form reply tabhi bhej sakte jab customer ne last 24h me message bheja ho.
        </div>
      </div>

      <ChatActivityDialog
        open={activityOpen}
        onOpenChange={setActivityOpen}
        contact={contact}
      />

      {/* Admin-only `managment_feedback` quick-send popup. Renders the
          editable body-param inputs ({{1}}/{{2}} pre-filled with admin's
          name + designation) + a live preview that matches the real
          approved template, and posts to
          POST /api/templates/managment-feedback/send. */}
      <Dialog open={mfOpen} onOpenChange={setMfOpen}>
        <DialogContent className="max-w-lg" data-testid="managment-feedback-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Megaphone size={18} weight="fill" className="text-[#7C3AED]" />
              Management Feedback
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="text-xs text-[var(--muted)]">
              Template: <code className="font-mono">managment_feedback</code>
              {" · "}
              To: <span className="font-semibold">+{contact?.phone}</span>
            </div>
            {mfLoadingPreview ? (
              <div className="flex items-center gap-2 text-sm text-[var(--muted)] py-3">
                <CircleNotch size={14} className="animate-spin" /> Loading template preview…
              </div>
            ) : (
              <>
                {mfPreview && (
                  <div className="rounded-md bg-[#dcf8c6] dark:bg-[#1f3a2a] border border-[var(--border)] p-3 text-sm whitespace-pre-wrap text-[#111] dark:text-[#e6e6e6] max-h-[280px] overflow-y-auto">
                    <div className="text-[10px] uppercase tracking-wide text-[#65676b] dark:text-[#aab] mb-1.5 font-semibold">
                      Preview
                    </div>
                    {/* Render preview with current param values */}
                    {(() => {
                      let rendered = mfPreview;
                      mfParams.forEach((val, idx) => {
                        const placeholder = `{{${idx + 1}}}`;
                        rendered = rendered.split(placeholder).join(val || placeholder);
                      });
                      return rendered;
                    })()}
                    {/* Template button preview */}
                    <div className="mt-3 pt-2 border-t border-[#11111122] space-y-1">
                      <div className="text-[10px] uppercase tracking-wide text-[#65676b] dark:text-[#aab] font-semibold mb-1">
                        Buttons
                      </div>
                      <div className="text-center text-xs py-1.5 rounded bg-white dark:bg-[#0f1a14] text-[#0095f6] font-medium">
                        Maybe some other time
                      </div>
                      <div className="text-center text-xs py-1.5 rounded bg-white dark:bg-[#0f1a14] text-[#0095f6] font-medium">
                        Yes, I'm available
                      </div>
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  {mfParams.map((val, idx) => (
                    <div key={idx} className="space-y-1">
                      <label className="text-xs font-medium text-[var(--muted)]">
                        {mfLabels[idx] || `Variable {{${idx + 1}}}`}
                        <code className="ml-1.5 font-mono text-[10px] text-[var(--muted)]">{`{{${idx + 1}}}`}</code>
                      </label>
                      <Input
                        value={val}
                        onChange={(e) => {
                          const next = [...mfParams];
                          next[idx] = e.target.value;
                          setMfParams(next);
                        }}
                        placeholder={mfLabels[idx] || `Value for {{${idx + 1}}}`}
                        data-testid={`mf-param-${idx}`}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setMfOpen(false)}
              disabled={mfSending}
              data-testid="mf-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              className="gap-1.5 bg-[#7C3AED] hover:bg-[#6D28D9] text-white"
              disabled={mfSending || mfLoadingPreview || !contact?.phone}
              onClick={async () => {
                if (!contact?.phone) return;
                setMfSending(true);
                try {
                  const r = await api.post("/templates/managment-feedback/send", {
                    phone: contact.phone,
                    body_params: mfParams.map((p) => (p || "").trim()),
                  });
                  toast.success("Feedback template sent", {
                    description: r?.data?.wamid ? `wamid: ${r.data.wamid}` : null,
                  });
                  setMfOpen(false);
                } catch (e) {
                  const msg =
                    e?.response?.data?.detail ||
                    e?.message ||
                    "Template send failed";
                  toast.error("Send failed", { description: msg });
                } finally {
                  setMfSending(false);
                }
              }}
              data-testid="mf-send-btn"
            >
              {mfSending ? (
                <>
                  <CircleNotch size={14} className="animate-spin" /> Sending
                </>
              ) : (
                <>
                  <PaperPlaneTilt size={14} weight="fill" /> Send to customer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
